import React, { useState } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { useLocale } from 'hooks';
import { Map, Marker, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import dayjs from 'dayjs';

import {
  CalendarIcon,
  CameraIcon,
  EnergyIcon,
  FactoryIcon,
  GlobeIcon,
  InstallerIcon,
  LocationIcon,
  MeterIcon,
  SuccessIcon,
  WeightIcon,
} from 'ui/icons';
import { AssetStatus, InfoBlock, ZoomableImage } from 'ui';
import { truncateString } from 'helpers';
import { Asset } from 'hooks/useAssets/useAssets.types';
import markerIcon from 'assets/images/marker.svg';

import { useStyles } from './AssetDetails.styles';

const marker = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon,
  iconSize: [33, 39],
  iconAnchor: [16, 39],
  popupAnchor: [1, -37],
});

export const AssetDetails = ({
  capacity,
  dateOfIssue,
  manufacturer,
  origin,
  weight,
  status,
  id,
  name,
  power,
  maxVoltage,
  latitude,
  longitude,
  address,
  digitalMeter,
  installer,
  dateOfInstallation,
  installationPhoto,
  verifier,
  dateOfVerification,
  certPhoto,
  comment,
}: Asset) => {
  const { container, mainContainer, assetId, assetName, iconStatus, mapContainer, showMap } = useStyles({
    status,
  });
  const [isMapOpened, setMapOpened] = useState(false);

  const { formatDate, formatMessage } = useLocale();

  const state = {
    lat: latitude ?? 0,
    lng: longitude ?? 0,
    zoom: 15,
  };

  const position = { lat: state.lat, lng: state.lng };

  const hasLocation = longitude && latitude;

  return (
    <Grid container>
      <Grid container alignItems="center" justify="center" alignContent="center" className={mainContainer}>
        <AssetStatus status={status} statusIconClassName={iconStatus} />
        <Grid item xs={12} alignItems="center" direction="column" container>
          <Typography className={assetId}>{truncateString({ maxLength: 20, string: id })}</Typography>
          <Typography className={assetName}>{name}</Typography>
        </Grid>
      </Grid>
      <Grid container direction="column" justify="space-between">
        <Grid item xs={12} className={container}>
          <InfoBlock
            items={[
              {
                text: manufacturer?.name ?? '',
                textIcon: FactoryIcon,
                label: formatMessage({ id: 'asset_details.manufacturer' }),
                labelIcon: SuccessIcon,
              },
              {
                text: origin,
                textIcon: GlobeIcon,
                labelIcon: SuccessIcon,
                label: formatMessage({ id: 'asset_details.origin' }),
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} className={container}>
          <InfoBlock
            items={[
              {
                text: formatDate ? formatDate(dateOfIssue) : '',
                label: formatMessage({ id: 'asset_details.dateOfIssue' }),
                textIcon: CalendarIcon,
              },
              {
                text: capacity,
                label: formatMessage({ id: 'asset_details.capacity' }),
                textIcon: EnergyIcon,
              },
              {
                text: weight,
                label: formatMessage({ id: 'asset_details.weight' }),
                textIcon: WeightIcon,
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} className={container}>
          <InfoBlock
            items={[
              {
                text: power,
                textIcon: FactoryIcon,
                label: formatMessage({ id: 'asset_details.power' }),
                labelIcon: SuccessIcon,
              },
              {
                text: maxVoltage,
                textIcon: GlobeIcon,
                labelIcon: SuccessIcon,
                label: formatMessage({ id: 'asset_details.maximumVoltage' }),
              },
            ]}
          />
        </Grid>
        {(hasLocation || address) && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: address ?? '',
                  label: formatMessage({ id: 'asset_details.location' }),
                  textIcon: LocationIcon,
                  labelIcon: SuccessIcon,
                  content: (
                    <Box display="flex" alignItems="flex-end" onClick={() => setMapOpened(o => !o)}>
                      <Typography className={showMap}>
                        {isMapOpened ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                        {formatMessage({ id: isMapOpened ? 'asset_details.closeMap' : 'asset_details.openMap' })}
                      </Typography>
                    </Box>
                  ),
                },
              ]}
            />
            {isMapOpened && (
              <Box className={mapContainer} data-testid="location-map">
                <Map center={position} zoom={state.zoom}>
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={position} icon={marker} />
                </Map>
              </Box>
            )}
          </Grid>
        )}
        {digitalMeter && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: digitalMeter ?? '',
                  label: formatMessage({ id: 'asset_details.eanMeter' }),
                  textIcon: MeterIcon,
                  labelIcon: SuccessIcon,
                },
              ]}
            />
          </Grid>
        )}
        {installer && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: installer?.name ?? '',
                  label: formatMessage({ id: 'asset_details.installer' }),
                  textIcon: InstallerIcon,
                  labelIcon: SuccessIcon,
                },
                {
                  text: dateOfInstallation ? dayjs(dateOfInstallation).format('YYYY-MM-DD') : '',
                  label: formatMessage({ id: 'asset_details.dateOfInstallation' }),
                  textIcon: CalendarIcon,
                  labelIcon: SuccessIcon,
                },
              ]}
            />
          </Grid>
        )}
        {installationPhoto && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: formatMessage({ id: 'asset_details.clickToZoom' }),
                  label: formatMessage({ id: 'asset_details.batteryInstallationPhoto' }),
                  textIcon: CameraIcon,
                  labelIcon: SuccessIcon,
                  content: installationPhoto && <ZoomableImage src={URL.createObjectURL(installationPhoto)} />,
                },
              ]}
            />
          </Grid>
        )}
        {verifier && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: verifier?.name ?? '',
                  label: formatMessage({ id: 'asset_details.verifier' }),
                  textIcon: InstallerIcon,
                  labelIcon: SuccessIcon,
                },
                {
                  text: dateOfVerification ? dayjs(dateOfVerification).format('YYYY-MM-DD') : '',
                  label: formatMessage({ id: 'asset_details.dateOfVerification' }),
                  textIcon: CalendarIcon,
                  labelIcon: SuccessIcon,
                },
              ]}
            />
          </Grid>
        )}
        {certPhoto && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: formatMessage({ id: 'asset_details.clickToZoom' }),
                  label: formatMessage({ id: 'asset_details.certificationPhoto' }),
                  textIcon: CameraIcon,
                  labelIcon: SuccessIcon,
                  content: certPhoto && <ZoomableImage src={URL.createObjectURL(certPhoto)} />,
                },
              ]}
            />
          </Grid>
        )}
        {comment && (
          <Grid item xs={12} className={container}>
            <InfoBlock
              items={[
                {
                  text: comment ? comment : formatMessage({ id: 'asset_details.noComment' }),
                  label: formatMessage({ id: 'asset_details.comment' }),
                  textIcon: CameraIcon,
                  labelIcon: SuccessIcon,
                },
              ]}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
